import { 
  Typography, 
  Paper, 
  Box, 
  Grid, 
  Chip, 
  Divider, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Storage, 
  Code, 
  Language, 
  Security, 
  Web, 
  ShoppingCart, 
  Analytics, 
  PublicOutlined, 
  BusinessCenter, 
  AutoAwesome, 
  Check 
} from '@mui/icons-material';

/**
 * Component to display detected website technologies
 */
const TechnologyDetection = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Ensure data is properly structured before processing
  if (!data || !data.technologies || !Array.isArray(data.technologies) || data.technologies.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: 3, mt: 3, borderRadius: theme.shape.borderRadius }}>
        <Typography variant="h6">No technology data available</Typography>
      </Paper>
    );
  }

  // Group technologies by type
  const techByType = data.technologies.reduce((acc, tech) => {
    const type = tech.type || 'other';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(tech);
    return acc;
  }, {});

  // Get a list of unique technology types and sort them in a logical order
  const typeOrder = [
    'frontend', 'backend', 'cms', 'ecommerce', 'server', 'hosting', 
    'cdn', 'security', 'analytics', 'marketing', 'database', 'protocol', 'registrar', 'other'
  ];
  
  const techTypes = Object.keys(techByType)
    .sort((a, b) => {
      const indexA = typeOrder.indexOf(a);
      const indexB = typeOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  // Icons for different technology types
  const typeIcons = {
    'frontend': <Code />,
    'backend': <Storage />,
    'cms': <Web />,
    'hosting': <PublicOutlined />,
    'server': <Storage />,
    'security': <Security />,
    'cdn': <Language />,
    'ecommerce': <ShoppingCart />,
    'analytics': <Analytics />,
    'marketing': <BusinessCenter />,
    'registrar': <BusinessCenter />,
    'protocol': <Language />,
    'database': <Storage />,
    'other': <AutoAwesome />
  };

  // Colors for confidence levels with fallbacks
  const confidenceColors = {
    'high': theme.palette.success?.main ? 'success' : 'primary',
    'medium': 'primary',
    'low': 'default'
  };

  // Get color value safely with fallback
  const getColorValue = (colorName) => {
    if (colorName === 'default') {
      return theme.palette.grey?.[500] || '#9e9e9e';
    }

    if (colorName === 'white') {
      return theme.palette.primary.contrastText;
    }

    
    return theme.palette[colorName]?.main || theme.palette.primary.main;
  };

  // Get color alpha value safely
  const getColorAlpha = (colorName) => {
    if (colorName === 'default') {
      return theme.palette.grey?.[100] || '#f5f5f5';
    }
    return theme.palette[colorName]?.light || theme.palette.primary.light;
  };
  
  // Get detection method chips
  const getDetectionMethodChips = () => {
    const chips = [];
    
    if (data.serverSideDetection) {
      chips.push(
        <Tooltip key="server" title="Used server-side detection for better accuracy">
          <Chip 
            label="Server-side" 
            color="secondary" 
            size="small"
            icon={<Check fontSize="small" />}
            sx={{ fontWeight: 'bold', mr: 1, mb: 1 }}
          />
        </Tooltip>
      );
    }
    
    if (data.clientSideDetection) {
      chips.push(
        <Tooltip key="client" title="Used client-side detection techniques">
          <Chip 
            label="Client-side" 
            color="info" 
            size="small"
            icon={<Check fontSize="small" />}
            sx={{ fontWeight: 'bold', mr: 1, mb: 1 }}
          />
        </Tooltip>
      );
    }
    
    if (data.detectionMethod) {
      chips.push(
        <Tooltip key="method" title={`Detection method: ${data.detectionMethod}`}>
          <Chip 
            label={data.detectionMethod}
            variant="outlined"
            size="small"
            sx={{ mr: 1, mb: 1 }}
          />
        </Tooltip>
      );
    }
    
    return chips;
  };
  
  // Get source display for a technology
  const getSourceDisplay = (tech) => {
    if (!tech.source) return null;
    
    const sources = Array.isArray(tech.source) ? tech.source : [tech.source];
    
    // Color mapping for sources
    const sourceColors = {
      'server-side': 'secondary',
      'server-full-tech-detect': 'secondary',
      'server-tech-detect': 'secondary',
      'client-side': 'info',
      'domain-analysis': 'info',
      'url-analysis': 'info',
      'fallback': 'default',
      'default': 'default'
    };
    
    // Only show the first source with a tooltip listing all
    const primarySource = sources[0];
    const sourceColor = sourceColors[primarySource] || 'default';
    
    return (
      <Tooltip title={`Sources: ${sources.join(', ')}`}>
        <Chip 
          label={primarySource}
          size="small"
          color={sourceColor}
          variant="outlined"
          sx={{ 
            height: 18, 
            fontSize: '0.6rem', 
            ml: 1,
            display: isMobile ? 'none' : 'inline-flex' // Hide on mobile to save space
          }}
        />
      </Tooltip>
    );
  };
  
  // Function to render domain and hosting information with more detailed display
  const renderDomainInfo = () => {
    if (!data.domain) return null;
    
    // Extract domain information
    const { registrar, hosting, nameservers, ips, createdDate, updatedDate, expiryDate } = data.domain || {};
    
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <Language sx={{ mr: 1 }} color="primary" />
          Domain Information
        </Typography>
        <Card variant="outlined" sx={{ borderRadius: theme.shape.borderRadius }}>
          <CardContent sx={{ p: isMobile ? 2 : 3, '&:last-child': { pb: isMobile ? 2 : 3 } }}>
            <Grid container spacing={2}>
              {registrar && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <BusinessCenter fontSize="small" color="primary" sx={{ mr: 1, mt: 0.5 }} />
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Typography variant="body2" component="div" fontWeight="bold">
                        Registrar
                      </Typography>
                      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
                        {registrar}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              
              {hosting && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <PublicOutlined fontSize="small" color="primary" sx={{ mr: 1, mt: 0.5 }} />
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Typography variant="body2" component="div" fontWeight="bold">
                        Hosting Provider
                      </Typography>
                      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
                        {hosting}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              
              {nameservers && nameservers.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Language fontSize="small" color="primary" sx={{ mr: 1, mt: 0.5 }} />
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Typography variant="body2" component="div" fontWeight="bold">
                        Name Servers
                      </Typography>
                      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
                        {Array.isArray(nameservers) 
                          ? nameservers.join(', ') 
                          : nameservers}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              
              {ips && ips.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Storage fontSize="small" color="primary" sx={{ mr: 1, mt: 0.5 }} />
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Typography variant="body2" component="div" fontWeight="bold">
                        IP Addresses
                      </Typography>
                      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
                        {Array.isArray(ips) ? ips.join(', ') : ips}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              
              {(createdDate || updatedDate || expiryDate) && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 2,
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    {createdDate && (
                      <Typography variant="caption" component="div">
                        <strong>Created:</strong> {new Date(createdDate).toLocaleDateString()}
                      </Typography>
                    )}
                    {updatedDate && (
                      <Typography variant="caption" component="div">
                        <strong>Updated:</strong> {new Date(updatedDate).toLocaleDateString()}
                      </Typography>
                    )}
                    {expiryDate && (
                      <Typography variant="caption" component="div">
                        <strong>Expires:</strong> {new Date(expiryDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Add technology website mapping
  const getTechnologyWebsite = (techName) => {
    const websiteMap = {
      // Frontend
      'React': 'https://reactjs.org',
      'Vue.js': 'https://vuejs.org',
      'Angular': 'https://angular.io',
      'jQuery': 'https://jquery.com',
      'Bootstrap': 'https://getbootstrap.com',
      'Tailwind CSS': 'https://tailwindcss.com',
      'JavaScript': 'https://developer.mozilla.org/en-US/docs/Web/JavaScript',
      'TypeScript': 'https://www.typescriptlang.org',
      'HTML': 'https://developer.mozilla.org/en-US/docs/Web/HTML',
      'CSS': 'https://developer.mozilla.org/en-US/docs/Web/CSS',
      
      // Backend
      'Node.js': 'https://nodejs.org',
      'PHP': 'https://php.net',
      'Python': 'https://python.org',
      'Ruby': 'https://www.ruby-lang.org',
      'Java': 'https://www.java.com',
      'ASP.NET': 'https://dotnet.microsoft.com/apps/aspnet',
      'Ruby on Rails': 'https://rubyonrails.org',
      'Django': 'https://www.djangoproject.com',
      'Laravel': 'https://laravel.com',
      
      // CMS
      'WordPress': 'https://wordpress.org',
      'Drupal': 'https://www.drupal.org',
      'Joomla': 'https://www.joomla.org',
      'Webflow': 'https://webflow.com',
      'Ghost': 'https://ghost.org',
      
      // Ecommerce
      'Shopify': 'https://www.shopify.com',
      'WooCommerce': 'https://woocommerce.com',
      'Magento': 'https://magento.com',
      
      // Hosting/CDN
      'Netlify': 'https://www.netlify.com',
      'Vercel': 'https://vercel.com',
      'Heroku': 'https://www.heroku.com',
      'AWS': 'https://aws.amazon.com',
      'Google Cloud': 'https://cloud.google.com',
      'Cloudflare': 'https://www.cloudflare.com',
      'Akamai': 'https://www.akamai.com',
      'Fastly': 'https://www.fastly.com',
      
      // Analytics
      'Google Analytics': 'https://analytics.google.com',
      'Mixpanel': 'https://mixpanel.com',
      'Segment': 'https://segment.com',
      
      // Security
      'SSL/TLS': 'https://www.cloudflare.com/learning/ssl/what-is-ssl/',
      'HTTPS': 'https://web.dev/why-https-matters/',
      
      // Database
      'MySQL': 'https://www.mysql.com',
      'PostgreSQL': 'https://www.postgresql.org',
      'MongoDB': 'https://www.mongodb.com',
      'Redis': 'https://redis.io',
      
      // Other common technologies
      'Webpack': 'https://webpack.js.org',
      'Babel': 'https://babeljs.io',
      'GraphQL': 'https://graphql.org',
      'Docker': 'https://www.docker.com',
      'Kubernetes': 'https://kubernetes.io',
      'Git': 'https://git-scm.com'
    };
    
    // Return the website URL or a search URL for unknown technologies
    return websiteMap[techName] || `https://www.google.com/search?q=${encodeURIComponent(techName + ' web technology')}`;
  };

  return (
    <Box sx={{ mt: -1 }}>
      {/* Add detection method header */}
      <Box sx={{ 
        mb: 3,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: 2
      }}>
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              color: theme.palette.text.primary,
              fontWeight: 600
            }}
          >
            <AutoAwesome color="primary" />
            Technology Stack
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mt: 0.5 }}
          >
            Detected {data.technologies.length} technologies
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1,
          alignItems: 'center'
        }}>
          {getDetectionMethodChips()}
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        {techTypes.map((type) => (
          <Grid item xs={12} sm={6} lg={4} key={type}>
            <Card 
              variant="outlined" 
              sx={{ 
                height: '100%',
                borderRadius: 1,
                transition: 'all 0.2s ease-in-out',
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${theme.palette.primary.main}25`,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ 
                p: { xs: 2, sm: 2.5 },
                '&:last-child': { pb: { xs: 2, sm: 2.5 } }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  pb: 1.5,
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                  <Box sx={{ 
                    mr: 1.5,
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.primary.main + '08',
                    p: 0.8,
                    borderRadius: 1
                  }}>
                    {typeIcons[type] || <AutoAwesome />}
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    component="div" 
                    sx={{ 
                      textTransform: 'capitalize',
                      fontWeight: 600,
                      color: theme.palette.text.primary
                    }}
                  >
                    {type}
                  </Typography>
                  <Chip 
                    label={techByType[type].length} 
                    size="small" 
                    color="primary" 
                    sx={{ 
                      ml: 'auto',
                      height: 20,
                      minWidth: 20,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: theme.palette.primary.main + '15',
                      color: theme.palette.primary.main,
                      border: 'none'
                    }}
                  />
                </Box>
                <List dense disablePadding>
                  {techByType[type].map((tech, index) => (
                    <ListItem 
                      key={index} 
                      disableGutters 
                      sx={{ 
                        py: 1,
                        px: 0.5,
                        borderBottom: index < techByType[type].length - 1 
                          ? `1px dashed ${theme.palette.divider}` 
                          : 'none',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        gap: isMobile ? 0.5 : 0
                      }}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          width: '100%',
                          gap: 1
                        }}
                      >
                        <ListItemIcon sx={{ 
                          minWidth: 28,
                          color: theme.palette.text.secondary
                        }}>
                          {typeIcons[tech.type] || typeIcons[type] || <AutoAwesome fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              flexWrap: 'wrap',
                              gap: 1
                            }}>
                              <Tooltip title={tech.name + (tech.version ? ` ${tech.version}` : '')}>
                                <Typography
                                  variant="body2"
                                  component="a"
                                  href={getTechnologyWebsite(tech.name)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ 
                                    fontWeight: 500,
                                    maxWidth: isMobile ? '180px' : isTablet ? '150px' : '180px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: theme.palette.text.primary,
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      color: theme.palette.primary.main,
                                      textDecoration: 'none'
                                    }
                                  }}
                                >
                                  {tech.name}
                                  {tech.version && (
                                    <Typography 
                                      component="span" 
                                      variant="caption" 
                                      sx={{ 
                                        ml: 0.5,
                                        color: theme.palette.text.secondary,
                                        fontWeight: 400
                                      }}
                                    >
                                      {tech.version}
                                    </Typography>
                                  )}
                                </Typography>
                              </Tooltip>
                              {!isMobile && getSourceDisplay(tech)}
                            </Box>
                          }
                          sx={{ margin: 0 }}
                        />
                        <Chip 
                          label={tech.confidence || 'medium'} 
                          size="small" 
                          color={confidenceColors[tech.confidence] || 'default'} 
                          sx={{ 
                            height: 20, 
                            fontSize: '0.7rem',
                            ml: 'auto',
                            minWidth: 45,
                            fontWeight: 500,
                            backgroundColor: getColorAlpha(confidenceColors[tech.confidence] || 'default'),
                            color: getColorValue('white'),
                            border: 'none'
                          }}
                        />
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {renderDomainInfo()}
      
      {data.error && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', borderRadius: theme.shape.borderRadius }}>
          <Typography variant="body2" color="error">
            Error: {data.error}
          </Typography>
        </Box>
      )}

      {data.timestamp && (
        <Box sx={{ 
          mt: 3, 
          pt: 2, 
          borderTop: `1px dashed ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: 1
        }}>
          <Typography variant="caption" color="textSecondary">
            Detection completed at {new Date(data.timestamp).toLocaleString()} 
            {data.detectionDuration && ` in ${data.detectionDuration.toFixed(2)}s`}
          </Typography>
          {data.domain && data.domain.domain && (
            <Typography variant="caption" color="textSecondary">
              Domain: {data.domain.domain}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TechnologyDetection;